import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Reconciles the mixed group timeline when invitations, uploads, or asynchronous
// matching add shared feed rows. Replacing the loaded window preserves occurrence
// order and lets every member converge on the same history.
export default class extends Controller {
  static values = { url: String, after: String, communityId: Number }

  connect() {
    this._onRestore = () => this.refresh()
    this._onVisibility = () => {
      if (document.visibilityState === "visible") this.refresh()
    }
    document.addEventListener("native:restore", this._onRestore)
    document.addEventListener("visibilitychange", this._onVisibility)

    this._subscription = consumer.subscriptions.create(
      { channel: "MatchingChannel" },
      {
        connected: () => this.refresh(),
        received: (data) => {
          if (!["community_matching_complete", "community_timeline_updated"].includes(data?.type)) return
          if (Number(data.community_id) !== this.communityIdValue) return
          this.refresh()
        }
      }
    )

    // Close the render/subscription race and reconcile Turbo cache restores.
    this.refresh()
  }

  disconnect() {
    document.removeEventListener("native:restore", this._onRestore)
    document.removeEventListener("visibilitychange", this._onVisibility)
    this._subscription?.unsubscribe()
  }

  async refresh() {
    if (!this.hasUrlValue || !this.hasAfterValue) return
    if (this._refreshing) {
      this._refreshAgain = true
      return
    }

    this._refreshing = true
    try {
      const loaded = this.element.querySelectorAll(".community-timeline-item").length
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("after", this.afterValue)
      url.searchParams.set("loaded", loaded)

      const response = await fetch(url, {
        headers: { "Accept": "text/vnd.turbo-stream.html" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Community refresh failed (${response.status})`)

      const stream = await response.text()
      if (stream.trim()) this._renderPreservingPosition(stream)

      const nextCursor = response.headers.get("X-Community-Updates-After")
      if (nextCursor) this.afterValue = nextCursor
    } catch (error) {
      console.warn("[community-updates] refresh failed", error)
    } finally {
      this._refreshing = false
      if (this._refreshAgain) {
        this._refreshAgain = false
        this.refresh()
      }
    }
  }

  _renderPreservingPosition(stream) {
    const visible = Array.from(this.element.querySelectorAll(".community-timeline-item"))
      .find((item) => item.getBoundingClientRect().bottom > 0)
    const anchorId = visible?.id
    const anchorTop = visible?.getBoundingClientRect().top
    const scrollBefore = window.scrollY

    window.Turbo.renderStreamMessage(stream)

    requestAnimationFrame(() => {
      const replacement = anchorId ? document.getElementById(anchorId) : null
      if (replacement && anchorTop != null) {
        window.scrollBy(0, replacement.getBoundingClientRect().top - anchorTop)
      } else if (scrollBefore > 0) {
        window.scrollTo(0, scrollBefore)
      }
    })
  }
}
